import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";

import { isEqual, isEmpty } from "lodash/lang";
import { isNull, isUndefined, sortBy, sumBy } from "lodash";
import { withTranslation } from "react-i18next";

import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import RatioBarCell from "@plannel/components/aggrid/RatioBarCell";
import ChartScatter from "@plannel/components/rechart/ChartScatter";
import Snackbar from "@plannel/components/Snackbar";
import salesComparisonService from "@plannel/services/ip/sales-comparison-service";
import debug from "@plannel/utils/debug-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ColorUtil from "@plannel/pages/utils/color-util";
import numberUtil from "@plannel/utils/number-util";
import reduxUtil from "@plannel/utils/redux-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";

const CURRENT_DATE = new ZDate();
const INITIAL_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth() + 1, 1);
const INITIAL_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const MAX_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const INITIAL_PERIOD = { start: INITIAL_START_DATE, end: INITIAL_END_DATE };

const { snackBarMessageColor } = ColorUtil;
const AMT_GRID = "AMT_GRID";
const QTY_GRID = "QTY_GRID";
const SalesComparison = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [periodFilter, setPeriodFilter] = useState(
    reduxViewState?.period ?
      { start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end) }
      : INITIAL_PERIOD
  );

  const [datas, setDatas] = useState([]);

  const [amtDatas, setAmtDatas] = useState([]);
  const [qtyDatas, setQtyDatas] = useState([]);
  const [qtyRange, setQtyRange] = useState({min: 0, max: 0});
  const [amtRange, setAmtRange] = useState({min: 0, max: 0});

  const average = useRef({ salesAmt: 0, salesQty: 0 });
  const amtGridRef = useRef(null);
  const qtyGridRef = useRef(null);
  const [amtGridLoaded, setAmtGridLoaded] = useState(false);
  const [qtyGridLoaded, setQtyGridLoaded] = useState(false);
  
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const defaultChartConfig = {
    tagField: "location", tagName: t("location"),
    xAxisField: "salesQty", xAxisName: t("salesQty"),
    yAxisField: "salesAmt", yAxisName: t("salesAmt"),
    zAxisField: "sku", zAxisName: t("activeSkus")
  }

  const numberFormatter = params => {
    if (isNull(params.value) || isUndefined(params.value) || !isFinite(params.value)) {
      return "";
    }
    return numberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  const ratioBarColumn = (color, field, range) => {
    return {
      comparator: (valueA, valueB) => valueA - valueB,
      valueGetter: ({ data }) => data[field],
      // 막대 위에 얹히던 sparkline 툴팁 → 그리드 자체 툴팁(생김새가 앱 전체와 통일된다)
      tooltipValueGetter: ({ value }) => numberFormatter({ value }),
      cellRenderer: RatioBarCell,
      cellRendererParams: {
        color, min: range.min, max: range.max,
        formatter: ({ value }) => numberUtil.formatNumberByAvg(value, average.current[field]),
      }
    }
  };

  const [qtyColumnDefs, setQtyColumnDefs] = useState([
    { headerName: "locDesc", field: "locDesc", type: ["nonEditableColumn"], flex: 1.5, hide: true },
    { headerName: "location", field: "location", type: ["nonEditableColumn"], flex: 2 },
    {
      headerName: "salesQty", field: "salesQty",
      type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 3
    },
  ]);
  
  const [amtColumnDefs, setAmtColumnDefs] = useState([
    { headerName: "locDesc", field: "locDesc", type: ["nonEditableColumn"], flex: 1.5, hide: true },
    { headerName: "location", field: "location", type: ["nonEditableColumn"], flex: 2 },
    {
      headerName: "salesAmt", field: "salesAmt",
      type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 3
    },
  ]);

  const defaultQtyGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: QTY_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultAmtGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: AMT_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};

    if (periodFilter?.start) {
      params.startDate = periodFilter.start;
    }

    if (periodFilter?.end) {
      params.endDate = periodFilter.end;
    }

    return params;
  };

  const setDataAndAverage = results => {
    if (isEmpty(results)) { return; }
    const descSalesAmtDatas = sortBy(results, "salesAmt").reverse();
    const descSalesQtyDatas = sortBy(results, "salesQty").reverse();

    setAmtDatas(descSalesAmtDatas);
    setQtyDatas(descSalesQtyDatas);

    setQtyRange({min: 0, max: descSalesQtyDatas[0].salesQty * 1.2});
    setAmtRange({min: 0, max: descSalesAmtDatas[0].salesAmt * 1.2});

    const amtSum = sumBy(results, "salesAmt");
    const qtySum = sumBy(results, "salesQty");
    const count = results.length;

    average.current = { salesAmt: amtSum / count, salesQty: qtySum / count };
  }

  const retrieveSalesComparison = () => {
    const params = getRequestParams();

    salesComparisonService.getAll(params)
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        const typeDatas = results.map(data => ({ ...data, type: t("location") }));
        setDatas(typeDatas);
        setDataAndAverage(results);
      })
      .catch(e => {
        debug(e);
      });
  };

  const setHighlightCell = (gridApi, node) => {
    gridApi.ensureIndexVisible(node.rowIndex, 'middle');
    setTimeout(() => { gridApi.flashCells({ rowNodes: [node], flashDelay: 2000, fadeDelay: 1000 }); }, 100);
  }

  const setGridHighlightCell = (gridApi, location) => {
    gridApi.forEachNode(node => {
      if (node?.data?.location && isEqual(node.data.location, location)) {
        setHighlightCell(gridApi, node);
      }
    });
  }

  const onScatterClick = data => {
    const { location } = data;
    if (location) {
      setGridHighlightCell(amtGridRef.current.api, location);
      setGridHighlightCell(qtyGridRef.current.api, location);
    }
  }

  const onGridReady = (params, gridId) => {
    if (gridId === AMT_GRID) {
      setAmtGridLoaded(true);
      GridUtils.setColumnDefs({ ...params, columnDefs: amtColumnDefs, viewName, gridId, initState: true });
    } else if (gridId === QTY_GRID) {
      setQtyGridLoaded(true);
      GridUtils.setColumnDefs({ ...params, columnDefs: qtyColumnDefs, viewName, gridId, initState: true });
    }
  }

  useEffect(() => {
    retrieveSalesComparison();

    return () => {
      setDatas([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter]);

  useEffect(() => {
    if(!isEmpty(amtDatas) && !isEmpty(qtyDatas)) {
      const qtyColDefs = [
        { headerName: "locDesc", field: "locDesc", type: ["nonEditableColumn"], flex: 1.5, hide: true },
        { headerName: "location", field: "location", type: ["nonEditableColumn"], flex: 2 },
        {
          headerName: "salesQty", field: "salesQty",
          type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 3,
          ...ratioBarColumn(ColorUtil.colorSet(1), "salesQty", qtyRange)
        },
      ];

      const amtColDefs = [
        { headerName: "locDesc", field: "locDesc", type: ["nonEditableColumn"], flex: 1.5, hide: true },
        { headerName: "location", field: "location", type: ["nonEditableColumn"], flex: 2 },
        {
          headerName: "salesAmt", field: "salesAmt",
          type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 3,
          ...ratioBarColumn(ColorUtil.colorSet(0), "salesAmt", amtRange)
        },
      ];

      setQtyColumnDefs(qtyColDefs);
      setAmtColumnDefs(amtColDefs);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amtDatas, qtyDatas, amtRange, qtyRange])

  useEffect(() => {
    if(!isEmpty(qtyColumnDefs) && !isEmpty(amtColumnDefs) && amtGridLoaded && qtyGridLoaded) {
      GridUtils.setColumnDefs({ ...qtyGridRef.current, columnDefs: qtyColumnDefs, viewName, gridId: QTY_GRID});
      GridUtils.setColumnDefs({ ...amtGridRef.current, columnDefs: amtColumnDefs, viewName, gridId: AMT_GRID});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtyColumnDefs, amtColumnDefs, amtGridLoaded, qtyGridLoaded]);

  return (
    <>
      <FilterContainer hasIcons={false}>
        <PeriodFilter viewName={viewName} period={periodFilter} setPeriod={setPeriodFilter} dateType={"month"} maxDate={MAX_END_DATE}/>
      </FilterContainer>      <Box id={"sales-comparison"} sx={{ display: "inline-flex", overflow:"auto" }} width="100%" height="100%">
        <Box sx={{ width: "50%", minWidth: "400px", height: "100%", mr: 1 }}>
          <ChartScatter
            scatterConfig={defaultChartConfig}
            data={datas}
            onScatterClick={onScatterClick}
            isRandomColor
          />
        </Box>
        <Box sx={{ width: "25%", minWidth: "300px", mr: 1, mt: 1, height: "calc(100% - 13px)" }}>
          <Box className="ag-theme-balham grid-area">
            <AgGridReact
              ref={amtGridRef}
              rowData={amtDatas}
              {...defaultAmtGridMemo}
              animateRows
              onGridReady={(params) => onGridReady(params, AMT_GRID)}
            />
          </Box>
        </Box>
        <Box sx={{ width: "25%", minWidth: "300px", mt: 1, height: "calc(100% - 13px)" }}>
          <Box className="ag-theme-balham grid-area">
            <AgGridReact
              ref={qtyGridRef}
              rowData={qtyDatas}
              {...defaultQtyGridMemo}
              animateRows
              onGridReady={(params) => onGridReady(params, QTY_GRID)}
            />
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(SalesComparison);